// ЗАГЛУШКИ ВИЗАРДА — экраны падения и 404 по борду CD `Life Map - Stub Screens v2.html` (дроп
// `drop-stubs-30-09/`, принят Кириллом 30.09; тикет `3eb19875cbb881bb9a04e503ee488d31`).
//
// Вёрстка — каркас `Wizard Desktop Frame v2`: колонка 390 логических, на вебе `zoom` 1.125
// (К1–К3), в полосе хрома только знак — степпера и счётчика шага нет. Линия и тёплое пятно на
// вебе идут во всю ширину окна, за колонкой: расширение К5, принятое Кириллом вместе с бордом.
//
// ⚠️ ЭКРАНЫ НЕ ХОДЯТ В `useWizard()` И НЕ МОНТИРУЮТСЯ В ОБОЛОЧКУ. Падение показывает
// `ErrorBoundary`, когда упало всё под ним — провайдер визарда в том числе. 404 показывает `App`
// ВМЕСТО провайдера (`s951.d1`): провайдер на старте пишет конверт и правит адрес, а на чужом пути
// ему делать нечего. Поэтому оба самодостаточны: строки из копи-узла, вид из своего css.
//
// ⛔ ВТОРОЙ КНОПКИ «НАЧАТЬ ЗАНОВО» НА ЭКРАНЕ ПАДЕНИЯ НЕТ — решение Кирилла `s945`, «не сейчас».

import { useId } from 'react';
import { ERROR_COPY } from './errorCopy.js';
import { NOT_FOUND_COPY } from './notFoundCopy.js';
import { crashArt, lostArt } from './stubArt.js';

// Две геометрии, как у борда: телефон рисуется в 390×132, веб — во всю ширину окна 1280×148.
// Видна одна, выбирает css по ширине окна.
const PHONE = { W: 390, H: 132 };
const WIDE = { W: 1280, H: 148 };

function StubArt({ W, H, variant, kind }) {
  const uid = useId().replace(/:/g, '');
  const id = `stub${variant}${uid}`;
  const lost = kind === 'lost';
  const art = lost ? lostArt(W, H) : crashArt(W, H);
  // 404: линия и пунктир красятся градиентом, который гаснет по x (стопы борда), а не амбером.
  const stroke = lost ? `url(#${id}l)` : '#D48A3C';
  return (
    <svg
      className={`stub-svg stub-svg-${variant}`}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      data-testid={`stub-art-${variant}`}
    >
      <defs>
        <linearGradient id={`${id}e`} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset=".12" stopColor="#fff" stopOpacity="1" />
          <stop offset=".88" stopColor="#fff" stopOpacity="1" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id={`${id}m`} maskUnits="userSpaceOnUse" x="0" y="0" width={W} height={H}>
          <rect x="0" y="0" width={W} height={H} fill={`url(#${id}e)`} />
        </mask>
        <linearGradient id={`${id}f`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#D48A3C" stopOpacity=".30" />
          <stop offset="1" stopColor="#D48A3C" stopOpacity="0" />
        </linearGradient>
        {lost && (
          <linearGradient id={`${id}l`} x1="0" x2={W} y1="0" y2="0" gradientUnits="userSpaceOnUse">
            {art.fade.map(([o, a]) => (
              <stop key={o} offset={o} stopColor="#D48A3C" stopOpacity={a} />
            ))}
          </linearGradient>
        )}
      </defs>
      <g mask={`url(#${id}m)`}>
        {art.ghosts.map((d, i) => (
          <path key={i} d={d} className="stub-gh stub-fi" style={{ '--dl': `${(0.1 + i * 0.12).toFixed(2)}s` }} />
        ))}
        <path
          d={art.fill}
          fill={`url(#${id}f)`}
          className={lost ? 'stub-fi stub-fill-lost' : 'stub-fi'}
          style={{ '--dl': '1.9s' }}
        />
        <path d={art.line} className="stub-ln stub-dr" stroke={stroke} pathLength="1" data-testid="stub-line" />
        {lost ? (
          <path
            d={art.tail}
            className="stub-ln stub-fi"
            stroke={stroke}
            strokeDasharray="3 6"
            style={{ '--dl': '1.7s' }}
            data-testid="stub-tail"
          />
        ) : (
          <path
            d={art.tail}
            className="stub-ln stub-fi"
            stroke={stroke}
            strokeOpacity=".32"
            strokeDasharray="2 5"
            style={{ '--dl': '2.1s' }}
            data-testid="stub-tail"
          />
        )}
      </g>
      {!lost && (
        <circle cx={art.dot.cx} cy={art.dot.cy} r="2.6" className="stub-brk" style={{ '--dl': '1.95s' }} data-testid="stub-break" />
      )}
    </svg>
  );
}

/** Общий каркас обеих заглушек: знак, колонка, рисунок, заголовок, строка, одна кнопка. */
function StubFrame({ kind, title, body, action, onAction }) {
  return (
    <div className="stub" data-testid={`stub-${kind}`} data-stub={kind}>
      <div className="stub-rail" data-testid="stub-brand">Life Map</div>
      <div className="stub-col">
        <div className="stub-scr">
          <div className="stub-msg">
            <div className="stub-art">
              <div className="stub-wash" />
              <div className="stub-art-layer">
                <StubArt {...PHONE} variant="phone" kind={kind} />
                <StubArt {...WIDE} variant="wide" kind={kind} />
              </div>
            </div>
            <h1 className="stub-ttl">{title}</h1>
            <p className="stub-bd">{body}</p>
          </div>
          <div className="stub-cta-row">
            <button type="button" className="wz-cta" onClick={onAction}>
              {action}
            </button>
          </div>
        </div>
      </div>
      <div className="stub-grain" />
    </div>
  );
}

/** Экран падения — то, что `Sentry.ErrorBoundary` показывает вместо упавшего визарда. */
export function ErrorFallback() {
  return (
    <StubFrame
      kind="crash"
      title={ERROR_COPY['wiz.error.title']}
      body={ERROR_COPY['wiz.error.body']}
      action={ERROR_COPY['wiz.error.action']}
      onAction={() => window.location.reload()}
    />
  );
}

/**
 * Экран 404 — неизвестный путь (`s951.d1`: Vercel переписывает все пути на приложение, `App`
 * решает, что путь чужой). Кнопка — на начало визарда `/` (`s944.d1`); конверт не трогается,
 * поэтому человек с сохранёнными ответами вернётся на свой шаг.
 */
export function NotFound() {
  return (
    <StubFrame
      kind="lost"
      title={NOT_FOUND_COPY['wiz.notfound.title']}
      body={NOT_FOUND_COPY['wiz.notfound.body']}
      action={NOT_FOUND_COPY['wiz.notfound.action']}
      onAction={() => window.location.assign('/')}
    />
  );
}
